/**
 * Brookfarm quick-add popup.
 *
 * Opens the variant picker a product card shows instead of adding, reproducing live's
 * Superstore "slim" quickshop. Everything this file does is selection: opening, closing, and
 * keeping the popup's hidden <select>, price and button in step with the chosen pill.
 *
 * It deliberately does NOT post. brookfarm-add-to-cart.js already captures every
 * `form[action*="/cart/add"]` on the page during the capture phase, so the popup's form goes
 * through the same Rebuy-safe path as the card and the PDP with no code here. Adding a second
 * submit handler is how the legacy theme ended up with three competing fetch patches
 * (BUG-018).
 *
 * Delegated at the document rather than bound per dialog, so cards rendered later — by the
 * Section Rendering API, or pagination — are covered without re-binding.
 */
(() => {
  if (window.__brookfarmQuickAddBound) return;
  window.__brookfarmQuickAddBound = true;

  const DIALOG = 'dialog[data-bf-quick-add]';

  /* Scoped to the opener's own card, not looked up by product id. The same product can appear
     twice on one page — the homepage slider and a "you may also like" row both render this
     card — and an id lookup would open the first match, which may be the other one. */
  const dialogFor = (opener) => opener.closest('.bf-card')?.querySelector(DIALOG) || null;

  const closeAll = () => document.querySelectorAll(`${DIALOG}[open]`).forEach((d) => d.close());

  /* Reflect the chosen pill into the parts of the popup that are not the pill itself. The
     pills carry their own variant data (see brookfarm-variant-pills.liquid), so there is no
     variants JSON to parse and no lookup to get wrong. */
  const applyVariant = (dialog, input) => {
    const data = input.dataset;

    const select = dialog.querySelector('[data-bf-qa-select]');
    if (select) select.value = data.bfVariantId;

    const price = dialog.querySelector('[data-bf-qa-price]');
    if (price && data.bfVariantPrice) price.textContent = data.bfVariantPrice;

    const compare = dialog.querySelector('[data-bf-qa-compare]');
    if (compare) {
      compare.textContent = data.bfVariantCompare || '';
      compare.hidden = !data.bfVariantCompare;
    }

    const button = dialog.querySelector('.bf-quick-add__atc');
    if (!button) return;

    const available = data.bfVariantAvailable === 'true';
    button.disabled = !available;

    const label = button.querySelector('[data-bf-atc-label]');
    if (!label) return;

    const next = available ? button.dataset.addLabel : button.dataset.soldOutLabel;
    if (next) label.textContent = next;

    /* brookfarm-add-to-cart.js stashes the idle label the first time it swaps in "Adding..."
       and restores it afterwards. Clearing the stash makes it re-read, so an add that follows
       a variant change restores THIS variant's label rather than the one that was showing
       when the shopper first pressed the button. */
    delete label.dataset.idleLabel;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-bf-quick-add-open]');
    if (opener) {
      const dialog = dialogFor(opener);
      if (!dialog) return;
      event.preventDefault();
      /* showModal, not show: it is what puts the dialog in the top layer and brings the focus
         trap, the inert background and Esc-to-close with it. */
      if (!dialog.open) dialog.showModal();
      return;
    }

    if (event.target.closest('[data-bf-quick-add-close]')) {
      event.target.closest(DIALOG)?.close();
      return;
    }

    /* A click on the backdrop reports the dialog element itself as its target — anything on
       the panel targets a descendant. That is the whole test for click-outside-to-close. */
    if (event.target.matches(DIALOG)) event.target.close();
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-bf-variant-id]');
    if (!input) return;

    /* The PDP's pills carry the same attributes, and they are not in a dialog. Requiring one
       is what keeps this handler off that page's picker, which resolves variants its own way. */
    const dialog = input.closest(DIALOG);
    if (dialog) applyVariant(dialog, input);
  });

  /* Close once the add lands, so Rebuy's flyout is not opening behind a popup that is still
     covering it. `subscribe` comes from pubsub.js, which is deferred — this file is a plain
     script in the body and runs first, so the binding waits for the parse to finish. */
  const bindClose = () => window.subscribe?.('cart-update', closeAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindClose, { once: true });
  } else {
    bindClose();
  }
})();
